import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { motion } from 'framer-motion'
import { useState, useEffect } from 'react'
import {
	type LoaderFunctionArgs,
	type ActionFunctionArgs,
	type MetaFunction,
	useLoaderData,
	useFetcher,
	data,
} from 'react-router'
import { CityCombobox } from '#app/components/ui/city-combobox'
import { VerifiedBadge } from '#app/components/ui/verified-badge'
import { getUser } from '#app/lib/firebase/auth'
import { getFirestoreInstance } from '#app/lib/firebase/firebase.server'
import { type City } from '#app/routes/api.cities'
import { getSeoMeta } from '#app/utils/seo'
import { type ProfileData } from '#types/database.types'

export async function loader({ request }: LoaderFunctionArgs) {
	try {
		// Get user authentication state
		const { user, headers } = await getUser(request)
		return data({ user }, { headers })
	} catch (error) {
		console.error('Error loading search page:', error)
		return data({ user: null })
	}
}

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	const intent = formData.get('intent')

	// Handle search intent
	if (intent === 'search') {
		const cityName = formData.get('city_name') as string

		// Validate and sanitize search input
		if (!cityName || cityName.length > 100) {
			return data({ searchResults: [], searchCity: '' })
		}

		// Get Firestore instance
		const db = getFirestoreInstance()

		// Firestore doesn't have ILIKE, so we'll do case-insensitive matching by:
		// 1. Get all profiles
		// 2. Filter in-memory (for MVP - can optimize later with Algolia or similar)
		const profilesSnapshot = await db
			.collection('profiles')
			.orderBy('created_at', 'desc')
			.get()

		const searchLower = cityName.toLowerCase()
		const matchingProfiles: ProfileData[] = []

		profilesSnapshot.docs.forEach((doc) => {
			const docData = doc.data()
			if (docData.city_name?.toLowerCase().includes(searchLower)) {
				matchingProfiles.push({
					id: doc.id,
					user_id: docData.user_id,
					email: docData.email || null,
					twitter_user_id: docData.twitter_user_id || '',
					x_handle: docData.x_handle,
					display_name: docData.display_name || docData.x_handle,
					description: docData.description || null,
					location: docData.location || null,
					avatar_url: docData.avatar_url || null,
					profile_image_url:
						docData.profile_image_url || docData.avatar_url || '',
					profile_banner_url: docData.profile_banner_url || null,
					city_name: docData.city_name,
					lat: docData.lat,
					lng: docData.lng,
					followers_count: docData.followers_count || 0,
					friends_count: docData.friends_count || 0,
					statuses_count: docData.statuses_count || 0,
					favourites_count: docData.favourites_count || 0,
					verified: docData.verified || false,
					protected: docData.protected || false,
					twitter_created_at: docData.twitter_created_at || '',
					lang: docData.lang || null,
					created_at:
						docData.created_at?.toDate?.()?.toISOString() ||
						new Date().toISOString(),
				})
			}
		})

		return data({ searchResults: matchingProfiles, searchCity: cityName })
	}

	// Invalid action
	return new Response('Invalid action', { status: 400 })
}

export const meta: MetaFunction = () => {
	return getSeoMeta({
		title: 'Search Indie Hackers — Indie World',
		description:
			'Search for indie hackers, founders, and makers by city. Discover creators building from specific locations around the world.',
		keywords:
			'search indie hackers, find makers, discover founders, location search, city makers, indie hacker directory',
		url: 'https://indieworld.dev/search',
	})
}

export const handle: SEOHandle = {
	getSitemapEntries: () => [
		{
			route: '/search',
			priority: 0.8,
			changeFrequency: 'daily',
		},
	],
}

export default function Search() {
	const loaderData = useLoaderData<typeof loader>()
	const searchFetcher = useFetcher<typeof action>()

	const [searchCity, setSearchCity] = useState('')
	const [searchResults, setSearchResults] = useState<ProfileData[]>([])
	const [selectedSearchCity, setSelectedSearchCity] = useState<City | null>(
		null,
	)

	// Handle search results
	useEffect(() => {
		if (
			searchFetcher.data &&
			typeof searchFetcher.data === 'object' &&
			'searchResults' in searchFetcher.data
		) {
			const data = searchFetcher.data as {
				searchResults: ProfileData[]
				searchCity: string
			}
			setSearchResults(data.searchResults)
			setSearchCity(data.searchCity)
		}
	}, [searchFetcher.data])

	// Auto-submit search when city is selected
	useEffect(() => {
		if (selectedSearchCity) {
			const formData = new FormData()
			formData.append('intent', 'search')
			formData.append('city_name', selectedSearchCity.name)
			void searchFetcher.submit(formData, { method: 'post' })
			// Reset selected city to allow re-selecting the same city
			setSelectedSearchCity(null)
		}
	}, [selectedSearchCity, searchFetcher])

	return (
		<div className="bg-background min-h-screen">
			<div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
				{/* Header */}
				<div className="mb-8">
					<h1 className="gradient-text mb-2 text-4xl font-bold">
						Search Indie Hackers
					</h1>
					<p className="text-text-muted text-lg">
						Find makers and builders from cities around the world
					</p>
				</div>

				{/* Search Bar */}
				<div className="mb-8">
					<CityCombobox
						placeholder="Search by city..."
						onCitySelect={setSelectedSearchCity}
					/>
				</div>

				{/* Results */}
				{searchCity && (
					<div className="mb-6">
						<h2 className="text-text text-xl font-semibold">{searchCity}</h2>
						<p className="text-text-muted mt-1 text-sm">
							{searchResults.length}{' '}
							{searchResults.length === 1 ? 'indie hacker' : 'indie hackers'}{' '}
							found
						</p>
					</div>
				)}

				{/* Results Grid */}
				<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
					{searchResults.map((profile, index) => (
						<motion.a
							key={profile.id}
							href={`https://x.com/${profile.x_handle}`}
							target="_blank"
							rel="noopener noreferrer"
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ delay: index * 0.05 }}
							className="bg-surface hover:bg-surface-hover group flex flex-col gap-3 rounded-xl p-4 transition-colors"
						>
							{/* Header with Avatar and Name */}
							<div className="flex items-start gap-3">
								<img
									src={
										profile.profile_image_url ||
										profile.avatar_url ||
										`https://unavatar.io/x/${profile.x_handle}`
									}
									alt={profile.x_handle}
									className="ring-border group-hover:ring-primary h-12 w-12 flex-shrink-0 rounded-full ring-2 transition-all"
								/>
								<div className="flex-1 overflow-hidden">
									<div className="flex items-center gap-1">
										<p className="text-text truncate font-semibold">
											{profile.display_name}
										</p>
										{profile.verified && (
											<VerifiedBadge size="sm" className="flex-shrink-0" />
										)}
									</div>
									<p className="text-text-muted truncate text-sm">
										@{profile.x_handle}
									</p>
								</div>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width="20"
									height="20"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
									className="text-text-muted group-hover:text-primary flex-shrink-0 transition-colors"
								>
									<path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
									<polyline points="15 3 21 3 21 9"></polyline>
									<line x1="10" y1="14" x2="21" y2="3"></line>
								</svg>
							</div>

							{/* Bio/Description */}
							{profile.description && (
								<p className="text-text line-clamp-2 text-sm">
									{profile.description}
								</p>
							)}

							{/* Stats and Join Date */}
							<div className="flex items-center gap-4 text-xs">
								<div className="text-text-muted">
									<span className="text-text font-semibold">
										{profile.followers_count.toLocaleString()}
									</span>{' '}
									Followers
								</div>
								<div className="text-text-muted">
									<span className="text-text font-semibold">
										{profile.friends_count.toLocaleString()}
									</span>{' '}
									Following
								</div>
								{profile.twitter_created_at && (
									<div className="text-text-muted ml-auto">
										Joined{' '}
										{new Date(profile.twitter_created_at).toLocaleDateString(
											'en-US',
											{
												month: 'short',
												year: 'numeric',
											},
										)}
									</div>
								)}
							</div>
						</motion.a>
					))}
				</div>

				{/* Empty State */}
				{searchCity && searchResults.length === 0 && (
					<div className="flex flex-col items-center justify-center py-12 text-center">
						<p className="text-text-muted text-lg">
							No indie hackers found in {searchCity}
						</p>
						<p className="text-text-muted mt-2">
							Be the first to add yourself!
						</p>
					</div>
				)}

				{/* No Search Yet State */}
				{!searchCity && (
					<div className="flex flex-col items-center justify-center py-12 text-center">
						<p className="text-text-muted text-lg">
							Enter a city name to discover indie hackers
						</p>
					</div>
				)}
			</div>
		</div>
	)
}
